import { api } from "@config/axios/axios.config";
import { SERVER_ROUTES } from "@constants/api.routes";

export interface ApplicationSessionChallanFee {
	uuid?: string;
	applicationProcessingFee?: number | null;
	technicalCoursesFee?: number | null;
	/** Technical courses count for package display (null/0 → default copy) */
	technicalCoursesCount?: number | null;
	vocationalCoursesFee?: number | null;
	/** Vocational courses count for package display (null/0 → default copy) */
	vocationalCoursesCount?: number | null;
	additionalPerCourseFee?: number | null;
	challanProcessingFee?: number | null;
	signUpFee?: number | null;
	penaltyFee?: number | null;
	changeOwnershipFee?: number | null;
	newAppChangeFee?: number | null;
	isActive?: boolean;
	sessionId?: number;
	eventId?: number;
}

export interface ApplicationSessionCourseStream {
	id: number;
	uuid: string;
	name: string;
	identifier: string;
	isActive: boolean;
}

export interface ApplicationSessionRegistrationEvent {
	uuid: string;
	name?: string;
	eventType?: string;
	startDate?: string;
	endDate?: string;
}

export interface ApplicationSession {
	id: number;
	uuid: string;
	createdAt: string;
	updatedAt: string;
	startDate: string;
	endDate: string;
	startYear: number;
	endYear: number;
	isActive: boolean;
	isCurrent: boolean;
	isClosed: boolean;
	label?: string;
	/** Course fees and count limits for this academic calendar */
	challanFee?: ApplicationSessionChallanFee | null;
	/** Streams open for registration in this academic calendar */
	courseStreams?: ApplicationSessionCourseStream[];
	courseStreamUuids?: string[];
	/** Registration event UUID for challan-initialize and related flows */
	registrationEventUuid?: string;
	registrationEventType?: string;
	registrationStartDate?: string;
	registrationEndDate?: string;
	registrationEvent?: ApplicationSessionRegistrationEvent | null;
}

export const REGISTRATION_EVENT_TYPE = {
	NEW: "new",
	RENEW: "renew",
} as const;

/** True when the academic calendar session is a renewal event (not first-time registration). */
export function isRenewRegistrationSession(session: ApplicationSession | null | undefined): boolean {
	const eventType = (
		session?.registrationEventType ??
		session?.registrationEvent?.eventType ??
		""
	).toLowerCase();
	return eventType === REGISTRATION_EVENT_TYPE.RENEW;
}

/** Resolve registration event UUID from an academic calendar session payload. */
export const getSessionRegistrationEventUuid = (
	session: ApplicationSession | null | undefined,
): string | null => {
	if (!session) return null;
	if (session.registrationEventUuid) return session.registrationEventUuid;
	if (session.registrationEvent?.uuid) return session.registrationEvent.uuid;
	return null;
};

interface PaginatedResponse<T> {
	data: T[];
	meta?: {
		page: number;
		limit: number;
		total: number;
		totalPages: number;
	};
}

export interface GetApplicationSessionsParams {
	/** When true, only calendars whose startYear or endYear equals the current calendar year */
	current?: boolean;
	/** When true, only calendars that have at least one linked certificate (past or current) */
	certified?: boolean;
	/** When true, prefer calendars suitable for institute renewal / update flows */
	preferRenew?: boolean;
	/** When true, only calendars open for institute renewal */
	renewal?: boolean;
}

/** Normalize academic-calendars payload after the axios interceptor unwraps `response.data`. */
const extractApplicationSessions = (payload: unknown): ApplicationSession[] => {
	if (Array.isArray(payload)) {
		return payload as ApplicationSession[];
	}

	if (payload && typeof payload === "object") {
		const root = payload as { data?: unknown };
		if (Array.isArray(root.data)) {
			return root.data as ApplicationSession[];
		}
		if (root.data && typeof root.data === "object") {
			const nested = root.data as { data?: unknown };
			if (Array.isArray(nested.data)) {
				return nested.data as ApplicationSession[];
			}
		}
	}

	return [];
};

export const applicationSessionsApi = {
	getApplicationSessions: async (params?: GetApplicationSessionsParams) => {
		const response = await api.get<PaginatedResponse<ApplicationSession>>(
			SERVER_ROUTES.INSTITUTES.SYSTEM_FILTERS.APPLICATION_SESSIONS,
			{
				params: {
					isActive: true,
					page: 1,
					limit: 100, // Get all active sessions
					...(params?.current ? { current: true } : {}),
					...(params?.certified ? { certified: true } : {}),
					...(params?.preferRenew ? { preferRenew: true } : {}),
					...(params?.renewal ? { renewal: true } : {}),
				},
			},
		);
		// Interceptor returns API body; support both `{ data: [] }` and `{ data: { data: [] } }`
		return extractApplicationSessions(response);
	},
};
